import { AiThinkingIcon } from "./aiThinkingIcon";
import { useEffect, useMemo, useRef, useState } from "react";
import { Progress } from "antd";
import _ from "lodash";
import { getAlertStatusStyles } from "lib/utils";
import { ALERT_PROCESS_STATUS_SDK, ALERT_STATUS_DISPLAY } from "lib/constants";
import moment from "moment";
import { motion } from "motion/react";

type ChoiceType = {
  id: string;
  isTimeout?: boolean;
  type: string;
};

export type AGIFlowType = {
  type: string; // Perception;Recognition;Decision;Action
  alertTime: number;

  location?: string;

  alertImgUrl?: string;
  tag?: string[];
  alertType?: string;
  description?: string;

  recommendedChoice?: ChoiceType;
  sendTo?: string;

  countdown?: number;
  countdownStart?: number;

  otherChoices?: ChoiceType[];
  reason?: string;

  actionText?: string;
};
const getTypeTitle = (type: string) => {
  switch (type) {
    case "Perception":
      return (
        <p className="text-[#0098A7] bg-[#E4F3F5] rounded-lg w-fit px-2 mb-1 font-semibold text-nowrap">
          {type}
        </p>
      );
    case "Recognition":
      return (
        <p className="text-[#974F0C] bg-[#FFF7D6] rounded-lg w-fit px-2 mb-1 font-semibold text-nowrap">
          {type}
        </p>
      );
    case "Decision":
      return (
        <p className="text-[#5E4DB2] bg-[#F3F0FF] rounded-lg w-fit px-2 mb-1 font-semibold text-nowrap">
          {type}
        </p>
      );
    case "Action":
      return (
        <p className="text-[#179954] bg-[#E4F6EC] rounded-lg w-fit px-2 mb-1 font-semibold text-nowrap">
          {type}
        </p>
      );
    default:
      return "";
  }
};

const transformChoicesToDisplay = (choices: string) => {
  switch (choices) {
    case "no_action":
      return "Do nothing";
    case "close_alert":
      return "Close the alert";
    case "investigate_alert":
      return "Investigate the alert";
    case "share_alert_to_admin":
      return "Notify property management";
    default:
      return choices;
  }
};
export default function AGIWorkflowCard({ data }: { data: AGIFlowType }) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [countdown, setCountdown] = useState(data.countdown || 0);

  const intervalRef = useRef(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        return prev >= 1 ? prev - 1 : 0;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    if (countdown === 0) {
      clearInterval(intervalRef.current);
    }
  }, [countdown]);

  const [status, setStatus] = useState("initial");

  useEffect(() => {
    if (status === "initial") {
      setTimeout(() => setStatus("enter"), getDelayTime());
      return;
    }
  }, [status]);

  const getDelayTime = () => {
    console.log("getDelayTime", data.type);
    switch (data.type) {
      case "Perception":
        return 0;
      case "Recognition":
        return 200;
      case "Decision":
        return 500;
      case "Action":
        return 800;
      default:
        return 0;
    }
  };

  const variants = {
    enter: {
      scale: [0.9, 1.03, 1],
      opacity: [0.1, 1],
      maxHeight: 500,
      backgroundColor: ["#F6F8FA"],
      ease: "easeInOut",
      transition: {
        duration: 0.8,
      },
    },
  };

  const renderAction = (action: string) => {
    switch (action) {
      case "false_alarm_alert":
        return (
          <>
            <p className="font-semibold">The status has been set to </p>
            <p
              style={getAlertStatusStyles(ALERT_PROCESS_STATUS_SDK.FalseAlarm)}
              className="text-sm  font-semibold rounded-md px-2 w-fit  mt-1"
            >
              {ALERT_STATUS_DISPLAY.FalseAlarm}
            </p>
          </>
        );
      case "close_alert":
        return (
          <>
            <p className="font-semibold">The status has been set to </p>
            <p
              style={getAlertStatusStyles(ALERT_PROCESS_STATUS_SDK.Close)}
              className="text-sm  font-semibold rounded-md px-2 w-fit  mt-1"
            >
              {ALERT_STATUS_DISPLAY.Close}
            </p>
          </>
        );
      case "investigate_alert":
        return (
          <>
            <p className="font-semibold">The status has been set to</p>
            <p
              style={getAlertStatusStyles(ALERT_PROCESS_STATUS_SDK.Investigate)}
              className="text-sm  font-semibold rounded-md px-2 w-fit mt-1"
            >
              {ALERT_STATUS_DISPLAY.Investigate}
            </p>
          </>
        );
      case "share_alert_to_admin":
        return (
          <>
            <p>Notification Sent To</p>
            <p className="text-base font-semibold text-[#222222] mt-1">
              {data.sendTo}
            </p>
          </>
        );

      default:
        return action;
    }
  };

  const getContent = () => {
    if (data.type === "Perception") {
      return (
        <>
          <div className="h-full flex flex-col  basis-[104px] flex-shrink-0">
            {getTypeTitle(data?.type)}
            <p className="text-sm text-[#222222] pl-1">
              {moment(data.alertTime).format("h:mm:ss A")}
            </p>
          </div>
          <div>
            <p className="text-[#717171] text-sm  font-semibold">Location</p>
            <p className="text-[#222222]">{data?.location || "-"}</p>
            <p className="text-[#717171] text-sm mt-2  font-semibold">
              Alert Type
            </p>
            <p className="text-[#222222]">
              {_.capitalize(data?.alertType) || "-"}
            </p>
          </div>
        </>
      );
    }
    if (data.type === "Recognition") {
      return (
        <>
          <div className="h-full flex flex-col  basis-[104px] flex-shrink-0">
            {getTypeTitle(data?.type)}
            <p className="text-sm text-[#222222] pl-1">
              {moment(data.alertTime).format("h:mm:ss A")}
            </p>

            <img
              src={data?.alertImgUrl}
              className="w-full aspect-video rounded-md mt-4"
            />
          </div>
          <div>
            <p className="text-[#717171] text-sm  font-semibold">Description</p>
            <div className="flex gap-2 flex-wrap mt-1">
              {data?.tag?.map((tag) => (
                <span className="text-[#222222] px-2 py-1 bg-[#eeeeee] rounded-lg mr-1">
                  {tag}
                </span>
              ))}
            </div>
            {/* <p className="text-[#717171] text-sm mt-2  font-semibold">
              Alert Type
            </p>
            <p className="text-[#222222]">{data?.alertType || "-"}</p>
            <p className="text-[#717171] text-sm mt-2  font-semibold">
              Description
            </p>
            <p className="text-[#222222]">{data?.description || "-"}</p> */}
          </div>
        </>
      );
    }

    if (data.type === "Decision") {
      return (
        <>
          <div className="h-full flex flex-col  basis-[104px] flex-shrink-0">
            {getTypeTitle(data?.type)}
            <p className="text-sm text-[#222222] pl-1">
              {moment(data.alertTime).format("h:mm:ss A")}
            </p>
            {/* <div className="mt-4 ml-4">
              <Progress
                type="circle"
                percent={((data.countdown - countdown) / data.countdown) * 100}
                format={() => `${countdown}s`}
                size={40}
              />
            </div> */}
          </div>
          <div className="flex-grow">
            <p className="text-[#717171] text-sm  font-semibold">
              Recommended Action
            </p>
            <p className="text-[#222222]">
              {transformChoicesToDisplay(data?.recommendedChoice?.type || "-")}
            </p>
            <p className="text-[#717171] text-sm mt-2 font-semibold">Reason</p>
            <p className="text-[#222222]">{data?.reason || "-"}</p>
          </div>
        </>
      );
    }

    if (data.type === "Action") {
      return (
        <>
          <div className="h-full flex flex-col  basis-[104px] flex-shrink-0">
            {getTypeTitle(data?.type)}
            <p className="text-sm text-[#222222] pl-1">
              {moment(data.alertTime).format("h:mm:ss A")}
            </p>
            {/* <div className="mt-4 ml-4">
              <Progress
                type="circle"
                percent={((data.countdown - countdown) / data.countdown) * 100}
                format={() => `${countdown}s`}
                size={40}
              />
            </div> */}
          </div>
          <div className="flex-grow text-[#717171] text-sm">
            {renderAction(data.actionText)}
          </div>
        </>
      );
    }
  };

  return (
    <motion.div
      className={
        "flex rounded-2xl w-full gap-4 overflow-hidden p-4 mb-4 last:mb-1"
      }
      ref={cardRef}
      initial={{
        opacity: 0.1,
        scale: 0.9,
        backgroundColor: "F6F8FA",
        maxHeight: 0,
      }}
      animate={status}
      variants={variants}
      // onAnimationStart={(e) => {
      //   console.log("onAnimationComplete", e);
      //   if (e === "enter") {
      //     cardRef.current?.scrollIntoView(false);
      //   }
      // }}
      layout
    >
      {getContent()}
    </motion.div>
  );
}
